import { useEffect, useRef, useState, type RefObject } from 'react';
import { Bookmark, Check, Copy, FileText, Image as ImageIcon, FileType, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type Konva from 'konva';
import type { AwarenessUser } from '../awareness/types';
import type { Shape } from '@frame/shared';
import { CURSOR_PALETTE } from '../styles/tokens';
import { deleteLocalBoard } from '../sync/localPersistence';
import { forgetRecentBoard } from '../home/recentBoards';
import { saveUserTemplate } from '../templates/registry';
import { exportJpeg } from '../exporters/image';
import { exportPdf } from '../exporters/pdf';
import { exportMarkdown, markdownBlob } from '../exporters/markdown';
import { exportFilename, triggerDownload } from '../exporters/download';

interface Props {
  user: AwarenessUser;
  onUpdate: (patch: Partial<Pick<AwarenessUser, 'name' | 'colorIndex'>>) => void;
  boardId: string;
  onClose: () => void;
  // Pixel anchor so the popover can sit just to the right of the button.
  anchorRect: DOMRect;
  // Current board shapes — passed in so "Save as template" can snapshot
  // them without the popover needing access to the Y.Doc directly.
  shapes: readonly Shape[];
  // Live Konva stage handle for JPEG / PDF exports. Held in a ref so the
  // popover doesn't need to mount-coordinate with Canvas.
  stageRef: RefObject<Konva.Stage | null>;
}

// Small floating panel anchored next to the toolbar gear icon. Two sections:
// Identity (rename + recolor, applied live to awareness) and This board
// (copy invite link, forget board). Closes on Esc or any click that falls
// outside the popover itself.
export function SettingsPopover({ user, onUpdate, boardId, onClose, anchorRect, shapes, stageRef }: Props) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [draftName, setDraftName] = useState(user.name);
  const [copied, setCopied] = useState(false);
  const [savedTemplateName, setSavedTemplateName] = useState<string | null>(null);

  useEffect(() => {
    setDraftName(user.name);
  }, [user.name]);

  // Outside-click on mousedown (capture) so clicks inside the popover never
  // race with the close.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const popover = popoverRef.current;
      if (!popover) return;
      if (e.target instanceof Node && popover.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commitName = () => {
    const next = draftName.trim();
    if (next && next !== user.name) onUpdate({ name: next });
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — silently no-op */
    }
  };

  const saveAsTemplate = () => {
    // window.prompt is the v1-cheap path; a richer modal can come later. The
    // shapes snapshot is taken at click time so the user's last edits are
    // included even if they haven't blurred the textarea.
    const name = window.prompt('Template name', `My template ${new Date().toLocaleDateString()}`);
    if (!name) return;
    const description = window.prompt('Short description (optional)', '') ?? '';
    const t = saveUserTemplate({ name, description, shapes: [...shapes] });
    setSavedTemplateName(t.name);
    window.setTimeout(() => setSavedTemplateName(null), 2000);
  };

  // Export actions — each triggers a browser download. `exporting` blocks
  // the buttons during the off-screen Konva render so a double-click can't
  // queue two snapshots in parallel.
  const [exporting, setExporting] = useState<'jpeg' | 'pdf' | 'markdown' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const runExport = async (kind: 'jpeg' | 'pdf' | 'markdown') => {
    setExportError(null);
    setExporting(kind);
    try {
      if (kind === 'markdown') {
        const md = exportMarkdown(shapes, { title: boardId });
        triggerDownload(markdownBlob(md), exportFilename(boardId, 'md'));
        return;
      }
      const stage = stageRef.current;
      if (!stage) {
        setExportError('Canvas not ready — try again in a moment.');
        return;
      }
      if (kind === 'jpeg') {
        await exportJpeg(stage, shapes, boardId);
      } else {
        await exportPdf(stage, shapes, boardId);
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(null);
    }
  };

  const forgetBoard = async () => {
    const confirmed = window.confirm(
      'Forget this board? Local board data will be deleted from this browser.',
    );
    if (!confirmed) return;
    forgetRecentBoard(boardId);
    await deleteLocalBoard(boardId);
    navigate('/');
  };

  // Anchor just to the right of the gear button, vertically aligned with its
  // bottom edge. The toolbar lives on the left edge so this opens into the
  // canvas region (always has room).
  const style: React.CSSProperties = {
    position: 'fixed',
    left: anchorRect.right + 8,
    bottom: window.innerHeight - anchorRect.bottom,
    zIndex: 20,
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Settings"
      style={style}
      className="
        w-[260px] p-3
        bg-bg-panel border border-border-default rounded-3 shadow-floating
        flex flex-col gap-4
      "
    >
      <section className="flex flex-col gap-2">
        <h3 className="text-12 font-medium text-fg-tertiary uppercase tracking-wide m-0">
          Identity
        </h3>
        <label className="flex flex-col gap-1">
          <span className="text-12 text-fg-secondary">Name</span>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitName();
              }
            }}
            maxLength={32}
            className="
              h-7 px-2
              bg-bg-app border border-border-default rounded-2
              text-13 text-fg-primary
              focus:outline-none focus:border-accent
            "
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-12 text-fg-secondary">Color</span>
          <div className="flex gap-1.5 flex-wrap">
            {CURSOR_PALETTE.map((color, idx) => (
              <button
                key={idx}
                type="button"
                aria-label={`Color ${idx + 1}`}
                aria-pressed={user.colorIndex === idx}
                onClick={() => onUpdate({ colorIndex: idx })}
                className="
                  w-6 h-6 rounded-full border-2
                  transition-transform duration-micro
                  hover:scale-110
                  focus-visible:outline-none focus-visible:shadow-focus
                "
                style={{
                  backgroundColor: color,
                  borderColor:
                    user.colorIndex === idx ? 'var(--color-fg-primary)' : 'transparent',
                }}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-12 font-medium text-fg-tertiary uppercase tracking-wide m-0">
          This board
        </h3>
        <button
          type="button"
          onClick={copyInvite}
          className="
            h-8 px-2.5 inline-flex items-center gap-2
            bg-bg-app hover:bg-bg-hover
            border border-border-default rounded-2
            text-13 text-fg-primary
            transition-colors duration-micro
          "
        >
          {copied ? (
            <Check size={14} strokeWidth={1.5} className="text-success" />
          ) : (
            <Copy size={14} strokeWidth={1.5} />
          )}
          {copied ? 'Copied' : 'Copy invite link'}
        </button>
        <ExportRow
          icon={<ImageIcon size={14} strokeWidth={1.5} />}
          label={exporting === 'jpeg' ? 'Saving JPEG…' : 'Export as JPEG'}
          onClick={() => runExport('jpeg')}
          disabled={shapes.length === 0 || exporting !== null}
        />
        <ExportRow
          icon={<FileType size={14} strokeWidth={1.5} />}
          label={exporting === 'pdf' ? 'Saving PDF…' : 'Export as PDF'}
          onClick={() => runExport('pdf')}
          disabled={shapes.length === 0 || exporting !== null}
        />
        <ExportRow
          icon={<FileText size={14} strokeWidth={1.5} />}
          label={exporting === 'markdown' ? 'Saving Markdown…' : 'Export as Markdown'}
          onClick={() => runExport('markdown')}
          disabled={shapes.length === 0 || exporting !== null}
        />
        {exportError && (
          <p className="text-12 text-error m-0">{exportError}</p>
        )}
        <button
          type="button"
          onClick={saveAsTemplate}
          disabled={shapes.length === 0}
          title={shapes.length === 0 ? 'Nothing to save — add shapes first' : 'Save as template'}
          className="
            h-8 px-2.5 inline-flex items-center gap-2
            bg-bg-app hover:bg-bg-hover
            border border-border-default rounded-2
            text-13 text-fg-primary
            transition-colors duration-micro
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-bg-app
          "
        >
          {savedTemplateName ? (
            <Check size={14} strokeWidth={1.5} className="text-success" />
          ) : (
            <Bookmark size={14} strokeWidth={1.5} />
          )}
          {savedTemplateName ? `Saved "${savedTemplateName}"` : 'Save as template'}
        </button>
        <button
          type="button"
          onClick={forgetBoard}
          className="
            h-8 px-2.5 inline-flex items-center gap-2
            bg-bg-app hover:bg-danger-subtle hover:text-danger
            border border-border-default rounded-2
            text-13 text-fg-primary
            transition-colors duration-micro
          "
        >
          <Trash2 size={14} strokeWidth={1.5} />
          Forget this board
        </button>
      </section>
    </div>
  );
}

// Shared shell for the export buttons — same look as "Copy invite link"
// but with a disabled state and progress label.
function ExportRow({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="
        h-8 px-2.5 inline-flex items-center gap-2
        bg-bg-app hover:bg-bg-hover
        border border-border-default rounded-2
        text-13 text-fg-primary
        transition-colors duration-micro
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-bg-app
      "
    >
      {icon}
      {label}
    </button>
  );
}
